// ── ChecklistItemForm ──
import { B } from "../../../shared/theme";
import { Btn, FInput, Field, Modal, ReadOnlyForm } from "../../../shared/ui";
import { useForm } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

export const ChecklistItemForm = ({ initial, readOnly }) => {
    const { role, modal, setModal, closeModal, saving, saveRec } = useApp();
    const [f, set] = useForm(initial || {});

    // Responsible Party → who may complete (mirrors server)
    const canCompleteItem = (() => {
      if (role.isAdmin) return true;
      switch ((f.responsibleparty||"").trim()) {
        case "BDO":              return !!(role.isunderwriter || role.isbdo);
        case "Underwriter":      return !!role.isunderwriter;
        case "Credit Committee": return !!role.istermfunding;
        case "CFO":              return !!role.istermfunding;
        default:                 return false;
      }
    })();

    const needsDoc  = f.documentrequired && !f.unrequiredocument;
    const completed = !!f.completeddate;
    const hasBox    = f.boxURL && String(f.boxURL).trim()!=="";
    const blocked   = needsDoc && completed && !hasBox;
    const lockCompletion = readOnly || !canCompleteItem;

    const submit = () => {
      if (blocked) return;
      saveRec("saveClosingChecklistItem", {...f}, "Edited", "ClosingChecklistItems", "Updated checklist item", modal.onDone);
    };

    return <Modal title={readOnly?"View Checklist Item":"Edit Checklist Item"} onClose={closeModal} wide
      onEdit={readOnly&&role.canEdit?()=>{ closeModal(); setModal({type:"checklistitem-edit",rec:initial,onDone:modal.onDone}); }:null}>
      <ReadOnlyForm readOnly={readOnly}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 24px" }}>
          <div style={{ gridColumn:"1/-1", fontSize:15, fontWeight:600, color:B.cyanDark, marginBottom:4 }}>{f.checklistitem}</div>
          <div style={{ gridColumn:"1/-1", fontSize:12, color:"var(--color-text-secondary)", marginBottom:12 }}>
            {f.checklistcategory} · Responsible: <b>{f.responsibleparty||"—"}</b>
          </div>

          <Field label="Completed Date">
            <FInput type="date" value={(f.completeddate||"").slice(0,10)} onChange={set("completeddate")} disabled={lockCompletion} />
          </Field>
          <Field label="Completed By">
            <div style={{ padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #ddd", background:"#f5f5f5", color:"#555" }}>
              {f.completedby || "—"}
            </div>
          </Field>
          <Field label="Document URL">
            <FInput value={f.boxURL||""} onChange={set("boxURL")} placeholder={needsDoc?"Required to complete":""} />
          </Field>
          <div />

          <div style={{ gridColumn:"1/-1", marginBottom:10 }}>
            <label style={{ fontSize:11, fontWeight:600, color:"var(--color-text-secondary)", textTransform:"uppercase", letterSpacing:"0.05em", display:"block", marginBottom:4 }}>Description</label>
            <textarea
              value={f.description||""}
              onChange={e=>set("description")(e.target.value)}
              disabled={readOnly}
              rows={5}
              style={{ width:"100%", boxSizing:"border-box", padding:"8px 10px", fontSize:13, lineHeight:1.5,
                      border:"1px solid var(--color-border-tertiary)", borderRadius:6, resize:"vertical",
                      fontFamily:"var(--font-sans)", background:"var(--color-background-primary)", color:"var(--color-text-primary)" }}
            />
          </div>

          <div style={{ gridColumn:"1/-1", display:"flex", gap:28, marginTop:4 }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}>
              <input type="checkbox" checked={!!f.unrequiredocument} onChange={e=>set("unrequiredocument")(e.target.checked)} disabled={readOnly} /> Waive document requirement
            </label>
          </div>
        </div>
      </ReadOnlyForm>

      {!readOnly && !canCompleteItem && <div style={{ color:B.amberDark, fontSize:12, marginTop:10 }}>
        Only the responsible party ({f.responsibleparty||"unassigned"}) can mark this item complete. You can still edit the description and document URL.
      </div>}
      {!readOnly && blocked && <div style={{ color:"#993C1D", fontSize:12, marginTop:10 }}>
        This item requires a document URL before it can be marked complete. Add a URL, or waive the requirement.
      </div>}

      <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
        <Btn onClick={closeModal}>{readOnly?"Close":"Cancel"}</Btn>
        {!readOnly&&<Btn disabled={saving||blocked} onClick={submit} variant="primary">{saving?"Saving...":"Save"}</Btn>}
      </div>
    </Modal>;
  };
